// ======================================================================
// ImsPage.jsx
// Report Vendite con Confronto Mensile e Intervalli Temporali
//
// Funzionalità:
// - Carica dati vendite per diversi mesi
// - Raggruppa per SKU, regione, provincia
// - Mostra trend e variazioni
// - Filtri avanzati (prodotto, regione, trend, quantità)
// - Esportazione CSV
// - Layout responsive: su mobile mostra solo dettagli espandibili
// - Confronto YoY: selezionando un mese, carica automaticamente l'anno precedente
// - Selezione intervalli temporali: carica tutti i mesi in un range (es. Gen2024-Giu2024)
// - Risultati aggregati: singola chiamata API per l'intero intervallo temporale
//
// Logica chiave:
// 1. `skuOptions`: mappa SKU → Nome prodotto (da API prodotti)
// 2. I dati grezzi sono trasformati e arricchiti con il nome prodotto
// 3. I dati sono "pivotati" per mostrare colonne per ogni mese
// 4. Supporto responsive: su mobile solo 4 colonne principali + espansione
// 5. Supporto intervalli: selezione data inizio/fine per caricare range di mesi
// 6. Risultati aggregati: caricamento dati con singola chiamata API per intervallo
// ======================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, shallowEqual } from "react-redux";
import Select from "react-select";
import { saveAs } from "file-saver";

// DevExtreme Grid (tabelle avanzate)
import {
  SortingState,
  IntegratedSorting,
  PagingState,
  IntegratedPaging,
  FilteringState,
  IntegratedFiltering,
  DataTypeProvider,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  Table,
  TableHeaderRow,
  TableFilterRow,
  PagingPanel,
} from "@devexpress/dx-react-grid-bootstrap4";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";

// React Bootstrap
import { Card, Badge, Button, Row, Col, ButtonGroup, InputGroup, FormControl } from "react-bootstrap";

// Material UI (icone e Tooltip)
import { Tooltip, IconButton } from "@material-ui/core";
import { GetApp, Search } from "@material-ui/icons";

// API
import * as apiReport from "../../api/imsdata";
import * as apiProdotti from "../../api/prodotti";
import * as apiUtenti from "../../api/users";
import { icons, roles, roleOptionsForSelect, colors, isAgente, isAdmin, isContabile } from "../../config/config";

// Componenti locali
import { DataCharts } from "./DataCharts";
import { getDatabaseNamePath } from "../../config/config";
import ImsGrid from "./ImsGrid";
import ImsPrintPdfButton from "./ImsPrintPdfButton";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Moduli estratti dal refactoring
import { selectStyles, generateMonthYearOptions, transformDataWithPeriod, pivotData, calculateDiffAndTrend, formatIsoDateForImsColumns, buildImsDynamicColumns } from "./ImsPage.utils";
import { aggregateGlobally, aggregateByProvince, aggregateByRegion, aggregateByLine, groupDataByRegion, groupDataByProvince, groupDataByLine } from "./ImsPage.aggregation";
import { AdvancedFilterCell, CustomHeaderCell } from "./ImsPage.filters";
import { ProductFormatter, TrendFormatter, DiffFormatter, PercentFormatter, NumberFormatter, CurrencyFormatter, CurrencyFormatterColored, CurrencyFormatterTrendColored } from "./ImsPage.formatters";
import { sortPeriodsChronologically, getMonthNumber } from "./ImsPeriodUtils";

// ======================================================================
// CUSTOM HOOK: useIsMobile
// ======================================================================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 534);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 534);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

// ======================================================================
// COMPONENTE PRINCIPALE
// ======================================================================
const ImsPage = () => {
  const user = useSelector((state) => state.auth.user, shallowEqual);

  const role = parseInt(user.idRole);
  const userIsAdmin = role === roles.admin || role === roles.gestore || role === roles.developer;
  const userIsContabile = role === roles.contabile;

  const userIsAgente = isAgente(role);

  const [expandedRow, setExpandedRow] = useState(null);
  const getRowKey = (row) => `${row.sku}-${row.regione}-${row.prov}`;
  const isMobile = useIsMobile();

  const isMounted = useRef(true);

  const notify = () => {
    toast.dismiss({ toastId: "groupsId" });
    toast.success("Seleziona un raggruppamento", {
      toastId: "groupsId",
      closeButton: true,
      icon: "\u2139\uFE0F",
      position: toast.POSITION.BOTTOM_CENTER,
      theme: "light",
      style: { fontWeight: "bold" },
    });
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const [activeSearchType, setActiveSearchType] = useState(null);
  const [rawRows, setRawRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [provOptions, setProvOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadedPeriods, setLoadedPeriods] = useState([]);
  const [aggregatedPeriods, setAggregatedPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [selectedStartPeriod, setSelectedStartPeriod] = useState(null);
  const [selectedEndPeriod, setSelectedEndPeriod] = useState(null);
  const [aggregationType, setAggregationType] = useState(null);
  const [enabledDataTypes, setEnabledDataTypes] = useState({ ims: true, farmacie: false, online: false });

  const toggleDataType = (type) => {
    setEnabledDataTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const pdfHiddenColumnNames = [];

  const [monthYearOptions] = useState(generateMonthYearOptions());

  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState({});

  useEffect(() => {
    if (userIsAgente) {
      apiUtenti
        .getUserByToken(user.authToken)
        .then((data) => {
          if (isMounted.current) {
            setSelectedUser({ value: data.email, label: data.firstname + " " + data.lastname, user_id: data.id });
          }
        })
        .catch((error) => {
          if (isMounted.current) {
            console.error("Errore nel caricamento utente:", error);
          }
        });
    }

    if (userIsAdmin || userIsContabile) {
      apiUtenti
        .getAll(user.authToken)
        .then((data) => {
          if (isMounted.current) {
            const filteredData = data.filter((agente) => {
              return [roles.agenteValderma, roles.agenteRoypet, roles.agenteRoydermal, roles.agenteCompleto].includes(
                parseInt(agente.role, 10),
              );
            });
            setUsersList(filteredData || []);
          }
        })
        .catch((error) => {
          if (isMounted.current) {
            console.error("Errore nel caricamento utenti:", error);
            setUsersList([]);
          }
        });
    }
  }, [user.authToken, userIsAgente, userIsAdmin, userIsContabile]);

  // ===== UTILITIES =====
  const formatIsoDate = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const formatMonthKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

  const lastDateOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0);
  };

  const areSameMonth = (a, b) => formatMonthKey(a) === formatMonthKey(b);

  const isPeriodInAggregatedRanges = (periodDate) => {
    return aggregatedPeriods.some((aggregated) => {
      const startDate = new Date(aggregated.startDate);
      const endDate = new Date(aggregated.endDate);
      const currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        if (areSameMonth(currentDate, periodDate)) {
          return true;
        }
        currentDate.setMonth(currentDate.getMonth() + 1);
      }
      return false;
    });
  };

  // ===== FILTRI PERSONALIZZATI =====
  const numericGreaterOrEqualPredicate = (value, filter) => {
    if (!filter || filter.value === "" || filter.value == null) return true;
    const rowValue = Number(value);
    const filterValue = Number(filter.value);
    if (isNaN(filterValue) || isNaN(rowValue)) return false;
    return rowValue >= filterValue;
  };

  const customFilterPredicate = useCallback((value, filter) => {
    if (!filter?.value) return true;
    const filterValue = filter.value;
    if (typeof filterValue === "string" && filterValue.includes(",")) {
      return filterValue.split(",").includes(String(value));
    }
    return String(value)
      .toLowerCase()
      .includes(String(filterValue).toLowerCase());
  }, []);

  const performSearch = useCallback((term, data) => {
    if (!term) return data;
    const lower = term.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some((val) =>
        String(val)
          .toLowerCase()
          .includes(lower),
      ),
    );
  }, []);

  // ======================================================================
  // CARICAMENTO PRODOTTI (SKU → Nome)
  // ======================================================================
  const loadProductOptions = useCallback(async () => {
    if (skuOptions.length > 0) return;
    if (!isMounted.current) return;

    setLoading(true);
    try {
      const response = await apiProdotti.getAll(user.authToken);
      const data = Array.isArray(response) ? response : response?.data || [];
      const productList = data.map((p) => ({
        value: String(p.sku),
        label: `${p.name.toLowerCase().includes(p.qta.toLowerCase()) ? p.name.split(p.qta)[0] : p.name} ${p.qta}`,
      }));

      if (isMounted.current) {
        setSkuOptions(productList);
      }
    } catch (error) {
      if (isMounted.current) {
        console.error("Errore caricamento prodotti:", error);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [user.authToken, skuOptions.length]);

  // ======================================================================
  // CARICA UN SINGOLO PERIODO
  // ======================================================================
  const $encrptKey = "S8Nox5loQR14GcoW5wWNFZRVOzRr3xDi";
  function xorString(string, key) {
    // 1. Applica XOR sul Base64 (solo caratteri ASCII sicuri)
    let result = "";

    for (let i = 0; i < string.length; i++) {
      result += String.fromCharCode(string.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    
    // 1. Converti stringa in Base64
    const base64String = btoa(unescape(encodeURIComponent(result)));
    return base64String;
  }

  const loadPeriod = async (date) => {
    const periodo = formatIsoDate(date);
    if (loadedPeriods.some((p) => areSameMonth(p, date))) return;

    try {
      const typeDataList = ["ims"];

      const promises = typeDataList.map(async (typeData) => {
        const data = {
          user_grant: xorString(selectedUser?.value ?? "", $encrptKey),
          date_start: periodo,
          date_end: formatIsoDate(lastDateOfMonth(date)),
          company: getDatabaseNamePath(),
          mock: false,
          type_data: typeData,
        };

        const response = await apiReport.getImsData(user.authToken, data);
        const transformed = apiReport.transformData(response);
        return transformDataWithPeriod(transformed, periodo, [typeData]);
      });

      const results = await Promise.all(promises);
      const allTransformed = results.flat();

      if (isMounted.current) {
        setRawRows((prev) => [...prev, ...allTransformed]);
        setLoadedPeriods((prev) => {
          const newPeriods = [...prev, date];
          return newPeriods.sort((a, b) => a - b);
        });
      }
    } catch (error) {
      if (isMounted.current) {
        console.error(`Errore caricamento dati per ${date.toISOString()}:`, error);
      }
    }
  };

  // ======================================================================
  // AGGIUNGI UN PERIODO (SINGOLO O CON CONFRONTO YoY)
  // ======================================================================
  const addPeriod = async (selectedPeriod, compareWithPreviousYear = false) => {
    if (!selectedPeriod || !isMounted.current) return;

    if (skuOptions.length === 0) {
      await loadProductOptions();
    }

    if (!isMounted.current) return;
    setLoading(true);

    try {
      const selectedDate = selectedPeriod.value;
      const current = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);

      if (compareWithPreviousYear) {
        const previous = new Date(current.getFullYear() - 1, current.getMonth(), 1);
        await Promise.all([loadPeriod(current), loadPeriod(previous)]);
      } else {
        await loadPeriod(current);
      }

      if (!activeSearchType && isMounted.current) {
        setActiveSearchType("single");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const addPeriodRangeYoY = async (startPeriod, endPeriod, aggregatedResults = false, maxRateMonthly = 1) => {
    if (!isMounted.current) return;

    const startDate = {
      label: startPeriod.label,
      value: new Date(startPeriod.value.getFullYear() - 1, startPeriod.value.getMonth(), 1),
    };
    const endDate = {
      label: startPeriod.label,
      value: new Date(endPeriod.value.getFullYear() - 1, endPeriod.value.getMonth(), 1),
    };
    if (aggregatedResults) {
      loadAggregatedResults(startDate, endDate);
    } else {
      if (endDate.value.getMonth() + 1 - startDate.value.getMonth() + 1 <= maxRateMonthly + 1)
        addPeriodRange(startDate, endDate);
    }
  };

  // ======================================================================
  // AGGIUNGI INTERVALLO DI PERIODI
  // ======================================================================
  const addPeriodRange = async (startPeriod, endPeriod) => {
    if (!startPeriod || !endPeriod || !isMounted.current) return;

    if (skuOptions.length === 0) {
      await loadProductOptions();
    }

    if (!isMounted.current) return;
    setLoading(true);

    try {
      const startDate = new Date(startPeriod.value.getFullYear(), startPeriod.value.getMonth(), 1);
      const endDate = new Date(endPeriod.value.getFullYear(), endPeriod.value.getMonth(), 1);

      if (startDate > endDate) {
        if (isMounted.current) {
          alert("La data di inizio deve essere precedente o uguale alla data di fine");
        }
        return;
      }

      const periodsToLoad = [];
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        periodsToLoad.push(new Date(currentDate));
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      await Promise.all(periodsToLoad.map((period) => loadPeriod(period)));
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // ======================================================================
  // CARICA RISULTATI AGGREGATI
  // ======================================================================
  const loadAggregatedResults = async (startPeriod, endPeriod) => {
    if (!startPeriod || !endPeriod || !isMounted.current) return;

    if (startPeriod === endPeriod) {
      addPeriodRange(selectedStartPeriod, selectedEndPeriod);
      addPeriodRangeYoY(selectedStartPeriod, selectedEndPeriod, false, 1);
      return;
    }

    if (activeSearchType && activeSearchType !== "aggregated") {
      alert("Puoi confrontare solo mesi singoli o solo periodi aggregati. Resetta per cambiare tipo.");
      return;
    }

    if (skuOptions.length === 0) {
      await loadProductOptions();
    }

    if (!isMounted.current) return;
    setLoading(true);
    try {
      const startDate = new Date(startPeriod.value.getFullYear(), startPeriod.value.getMonth(), 1);
      const endDate = new Date(endPeriod.value.getFullYear(), endPeriod.value.getMonth(), 1);

      if (startDate > endDate) {
        alert("La data di inizio deve essere precedente o uguale alla data di fine");
        return;
      }

      const startLabel =
        monthYearOptions.find((opt) => areSameMonth(opt.value, startDate))?.label || formatIsoDate(startDate);
      const endLabel =
        monthYearOptions.find((opt) => areSameMonth(opt.value, endDate))?.label ||
        formatIsoDate(lastDateOfMonth(endDate));
      const aggregatedPeriod = `${startLabel} - ${endLabel}`;

      try {
        const typeDataList = ["ims"];

        const promises = typeDataList.map(async (typeData) => {
          const data = {
            user_grant: xorString(selectedUser?.value ?? "", $encrptKey),
            date_start: formatIsoDate(startDate),
            date_end: formatIsoDate(lastDateOfMonth(endDate)),
            company: getDatabaseNamePath(),
            mock: false,
            type_data: typeData,
          };

          const response = await apiReport.getImsData(user.authToken, data);
          const transformed = apiReport.transformData(response);
          return transformDataWithPeriod(transformed, aggregatedPeriod, [typeData]);
        });

        const results = await Promise.all(promises);
        const allTransformed = results.flat();

        if (!activeSearchType && isMounted.current) {
          setActiveSearchType("aggregated");
        }

        if (isMounted.current) {
          setRawRows((prev) => [...prev, ...allTransformed]);
          setAggregatedPeriods((prev) => [...prev, { startDate, endDate, label: aggregatedPeriod }]);
        }
      } catch (error) {
        if (isMounted.current) {
          console.error(
            `Errore caricamento dati aggregati per ${startDate.toISOString()} - ${endDate.toISOString()}:`,
            error,
          );
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const calculatePeriods = (startPeriod, endPeriod, isAggregated) => {
    if (!startPeriod || !endPeriod || !isMounted.current) return;

    if (!isAggregated || areSameMonth(startPeriod.value, endPeriod.value)) {
      addPeriodRange(selectedStartPeriod, selectedEndPeriod);
      addPeriodRangeYoY(selectedStartPeriod, selectedEndPeriod, false, 1);
      return;
    }
    loadAggregatedResults(selectedStartPeriod, selectedEndPeriod);
    addPeriodRangeYoY(selectedStartPeriod, selectedEndPeriod, true, 1);
  };

  // ======================================================================
  // AGGIORNA OPZIONI FILTRI (regioni, province)
  // ======================================================================
  const updateFilterOptions = useCallback((allRows) => {
    const regions = [...new Set(allRows.map((r) => r.regione))].filter(Boolean).sort();
    const provs = [...new Set(allRows.map((r) => r.prov))].filter(Boolean).sort();

    setRegionOptions(regions.map((r) => ({ value: r, label: r })));
    setProvOptions(provs.map((p) => ({ value: p, label: p })));
  }, []);

  // ======================================================================
  // EFFETTO PRINCIPALE: rawRows → filteredRows
  // ======================================================================
  useEffect(() => {
    if (rawRows.length === 0) {
      if (isMounted.current) {
        setFilteredRows([]);
      }
      return;
    }

    const enabledTypes = Object.keys(enabledDataTypes).filter((key) => enabledDataTypes[key]);
    const typeFilteredRows = rawRows.filter((row) => {
      if (!row.type_data) return true;
      return enabledTypes.includes(row.type_data);
    });

    let processedData = pivotData(typeFilteredRows);

    if (aggregationType === "province") {
      processedData = aggregateByRegion(processedData);
    } else if (aggregationType === "region") {
      processedData = aggregateByProvince(processedData);
    } else if (aggregationType === "global") {
      processedData = aggregateGlobally(processedData);
    } else if (aggregationType === "line") {
      processedData = aggregateByLine(processedData);
    }

    const withStats = processedData.map((row) => {
      const hasAggregated = Object.keys(row.periodi).some((p) => p.includes(" - "));
      const { diff, percentage, trend } = calculateDiffAndTrend(row.periodi, hasAggregated);

      const found = skuOptions.find((opt) => String(opt.value) === String(row.sku));

      return {
        ...row,
        productName: found ? found.label : `SKU ${row.sku}`,
        diff,
        percentage,
        trend,
      };
    });

    const filtered = performSearch(searchValue, withStats);
    if (isMounted.current) {
      setFilteredRows(filtered);
      setCurrentPage(0);
      updateFilterOptions(rawRows);
    }
  }, [
    rawRows,
    skuOptions,
    searchValue,
    performSearch,
    updateFilterOptions,
    aggregationType,
    enabledDataTypes,
  ]);

  // ======================================================================
  // CARICAMENTO INIZIALE
  // ======================================================================
  useEffect(() => {
    if (isMounted.current) {
      loadProductOptions();
    }
  }, [loadProductOptions]);

  // ======================================================================
  // COLONNE DINAMICHE
  // ======================================================================
  const dynamicColumns = useMemo(() => {
    return buildImsDynamicColumns({ rawRows, monthYearOptions, layoutMobile: isMobile, aggregationType });
  }, [rawRows, monthYearOptions, isMobile, aggregationType]);

  const printColumns = useMemo(() => {
    return buildImsDynamicColumns({ rawRows, monthYearOptions, layoutMobile: false, aggregationType });
  }, [rawRows, monthYearOptions, aggregationType]);

  const columnExtensions = useMemo(() => {
    const periods = sortPeriodsChronologically([...new Set(rawRows.map((r) => r.periodo))]);
    const extensions = [
      {
        columnName: "productName",
        align: "center",
        wordWrapEnabled: true,
        predicate: customFilterPredicate,
      },
    ];

    if (periods.length > 1) {
      extensions.push(
        { columnName: "diff", align: "center", predicate: numericGreaterOrEqualPredicate, width: !isMobile ? 80 : null },
        {
          columnName: "percentage",
          align: "center",
          width: !isMobile ? 80 : null,
          predicate: numericGreaterOrEqualPredicate,
        },
        { columnName: "trend", align: "center", width: !isMobile ? 80 : null },
        { columnName: "linea", align: "center" },
      );
    }

    extensions.push(...periods.map((p) => ({ columnName: `qty_${p}`, align: "center", width: !isMobile ? 80 : null })));
    extensions.push(
      ...periods.map((p) => ({ columnName: `fatturato_${p}`, align: "center", width: !isMobile ? 120 : null })),
    );

    if (aggregationType === "line") {
      extensions.push({ columnName: "regione", align: "center", predicate: customFilterPredicate });
    }

    return extensions;
  }, [rawRows, aggregationType]);

  // ======================================================================
  // ESPORTA IN CSV
  // ======================================================================
  const exportToCSV = useCallback(() => {
    const headers = dynamicColumns.map((c) => c.title).join(",");
    const rows = filteredRows.map((row) =>
      dynamicColumns
        .map((c) => {
          if (c.name.startsWith("qty_")) {
            const period = c.name.replace("qty_", "");
            return row.periodi?.[period] ?? 0;
          }
          if (c.name.startsWith("fatturato_")) {
            const period = c.name.replace("fatturato_", "");
            return row.fatturato?.[period] ?? 0;
          }
          return row[c.name] ?? "";
        })
        .join(","),
    );
    const csv = [headers, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `report-vendite-${new Date().toISOString().split("T")[0]}.csv`);
  }, [filteredRows, dynamicColumns]);

  // ======================================================================
  // PROVIDER PER TIPI DI DATO (usano formatters importati)
  // ======================================================================
  const useTrendColorForFatturato = false;

  const ProductTypeProvider = () => (
    <DataTypeProvider
      for={["productName"]}
      formatterComponent={(props) => (
        <ProductFormatter
          {...props}
          isMobile={isMobile}
          expandedRow={expandedRow}
          getRowKey={getRowKey}
          setExpandedRow={setExpandedRow}
        />
      )}
    />
  );

  const TrendTypeProvider = () => <DataTypeProvider for={["trend"]} formatterComponent={TrendFormatter} />;
  const DiffTypeProvider = () => <DataTypeProvider for={["diff"]} formatterComponent={DiffFormatter} />;
  const PercentTypeProvider = () => <DataTypeProvider for={["percentage"]} formatterComponent={PercentFormatter} />;

  const NumberTypeProvider = () => (
    <DataTypeProvider
      for={dynamicColumns.filter((c) => c.name.startsWith("qty_")).map((c) => c.name)}
      formatterComponent={NumberFormatter}
    />
  );

  const CurrencyTypeProvider = () => (
    <DataTypeProvider
      for={dynamicColumns.filter((c) => c.name.startsWith("fatturato_")).map((c) => c.name)}
      formatterComponent={CurrencyFormatter}
    />
  );

  // ======================================================================
  // ROW COMPONENT (riga espandibile con dettagli + grafici)
  // ======================================================================
  const RowComponent = ({ row, children, ...restProps }) => {
    const chartData = useMemo(() => {
      if (expandedRow === getRowKey(row)) {
        return [
          {
            ...row,
            product: row.productName,
            diff: row.diff,
            percentage: row.percentage,
          },
        ];
      }
      return [];
    }, [expandedRow, row]);

    const periodColumns = useMemo(() => {
      return sortPeriodsChronologically(Object.keys(row.periodi || {}));
    }, [row.periodi]);

    return (
      <React.Fragment>
        <Table.Row {...restProps} row={row}>
          {children}
        </Table.Row>
        {expandedRow === getRowKey(row) && (
          <tr>
            <td
              colSpan={dynamicColumns.length}
              style={{ padding: "1rem", borderTop: "none", backgroundColor: "rgba(225,255,255,0.5)" }}
            >
              <div className="p-2 bg-light border-dark rounded">
                <h3 className="mb-3">Dettagli {row.productName}</h3>
                <Row>
                  <Col xs={6}>
                    <strong>Regione:</strong> {row.regione}
                  </Col>
                  <Col xs={6}>
                    <strong>Provincia:</strong> {row.prov}
                  </Col>
                </Row>

                <div className="mt-3">
                  {Object.entries(row.periodi || {})
                    .sort(([periodoA], [periodoB]) => {
                      return (
                        sortPeriodsChronologically([periodoA, periodoB]).indexOf(periodoA) -
                        sortPeriodsChronologically([periodoA, periodoB]).indexOf(periodoB)
                      );
                    })
                    .map(([periodo, qty], index, entries) => {
                      const label = periodo.includes(" - ")
                        ? periodo
                        : monthYearOptions.find((opt) => formatIsoDateForImsColumns(opt.value) === periodo)?.label ||
                          periodo;
                      const prevQty = index > 0 ? entries[index - 1][1] : null;
                      let variant = "";
                      if (prevQty !== null) {
                        if (qty > prevQty) variant = "success";
                        else if (qty < prevQty) variant = "danger";
                      }
                      return (
                        <Badge className="m-1 border" variant={variant} key={periodo}>
                          {label}: {qty} unità
                        </Badge>
                      );
                    })}
                </div>

                {chartData.length > 0 && (
                  <div className="mt-4">
                    <DataCharts data={chartData} periodColumns={periodColumns} />
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // ======================================================================
  // RENDER
  // ======================================================================
  return (
    <Card className="card-custom utter-b shadow-sm">
      <Card.Header className="border-0 py-4 p-0">
        <Row className="align-items-center w-100 m-0">
          <Col md={4}>
            <h3 className="font-weight-bolder text-dark mb-0">Report Vendite</h3>
            <p className="text-muted mb-0 mt-1">
              {filteredRows.length} record trovati {searchValue && `(${rawRows.length} totali)`}
            </p>
          </Col>
          <Col md={8}>
            <div className="d-flex justify-content-end align-items-center">
              {!userIsAgente && (
                <Select
                  placeholder="Seleziona agente..."
                  className="form-select"
                  isClearable
                  styles={selectStyles}
                  value={selectedUser || ""}
                  onChange={(e) => {
                    if (isMounted.current) {
                      setRawRows([]);
                      setLoadedPeriods([]);
                      setAggregatedPeriods([]);
                      setFilteredRows([]);
                      setSelectedPeriod(null);
                      setSelectedStartPeriod(null);
                      setSelectedEndPeriod(null);
                      setActiveSearchType(null);
                      setAggregationType(null);
                      setSelectedUser(e);
                    }
                  }}
                  options={usersList.map((user) => ({
                    value: user.email,
                    label: user.fullname,
                    id: user.id,
                  }))}
                />
              )}

              <InputGroup className="me-3 ml-1" style={{ maxWidth: "250px" }}>
                <InputGroup.Prepend>
                  <InputGroup.Text>
                    <Search style={{ fontSize: 16 }} />
                  </InputGroup.Text>
                </InputGroup.Prepend>
                <FormControl
                  placeholder="Cerca in tutti i campi..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  size="md"
                />
                {searchValue && (
                  <InputGroup.Append>
                    <Button variant="outline-secondary" size="sm" onClick={() => setSearchValue("")}>
                      ×
                    </Button>
                  </InputGroup.Append>
                )}
              </InputGroup>

              <ButtonGroup className="me-3 ml-1">
                {rawRows.length > 0 && (
                  <Tooltip title="Esporta in CSV">
                    <Button variant="outline-success" size="md" onClick={exportToCSV} disabled={rawRows.length === 0}>
                      <GetApp className="me-2" style={{ fontSize: 16 }} />
                      Scarica
                    </Button>
                  </Tooltip>
                )}
              </ButtonGroup>
            </div>
          </Col>

          <Col md={12} className="mt-3 pt-3">
            {/* Sezione Intervallo Temporale */}
            <Row className="align-items-center pt-3">
              <Col xs={12}>
                <h6 className="text-muted mb-2">📊 Selezione Intervallo Temporale</h6>
              </Col>
              <Col xs={12} md={3} className="mb-2">
                <Select
                  options={monthYearOptions.filter(
                    (opt) =>
                      !loadedPeriods.some((p) => areSameMonth(p, opt.value)) &&
                      !isPeriodInAggregatedRanges(opt.value),
                  )}
                  placeholder="Mese di inizio..."
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  onChange={(e) => {
                    setSelectedStartPeriod(e);
                    setSelectedEndPeriod(e);
                  }}
                  value={selectedStartPeriod}
                  isDisabled={loading}
                />
              </Col>
              <Col xs={12} md={3} className="mb-2">
                <Select
                  options={monthYearOptions.filter(
                    (opt) =>
                      !loadedPeriods.some((p) => areSameMonth(p, opt.value)) &&
                      !isPeriodInAggregatedRanges(opt.value),
                  )}
                  placeholder="Mese di fine..."
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  onChange={setSelectedEndPeriod}
                  value={selectedEndPeriod}
                  isDisabled={loading}
                />
              </Col>
              <Col xs={12} md={2} className="mb-2">
                <div className="d-flex flex-column gap-2">
                  <Button
                    variant="info"
                    size="md"
                    onClick={() => {
                      if (selectedStartPeriod && selectedEndPeriod) {
                        calculatePeriods(selectedStartPeriod, selectedEndPeriod, true);
                        setSelectedStartPeriod(null);
                        setSelectedEndPeriod(null);
                        notify();
                      }
                    }}
                    disabled={!selectedStartPeriod || !selectedEndPeriod || loading}
                    className="w-100"
                  >
                    Aggiungi Periodo
                  </Button>
                </div>
              </Col>
              <Col xs={12} md={2} className="mb-2">
                <div className="d-flex flex-column gap-2">
                  <Button
                    variant="success"
                    size="md"
                    onClick={() => {
                      if (selectedStartPeriod && selectedEndPeriod) {
                        calculatePeriods(selectedStartPeriod, selectedEndPeriod, false);
                        setSelectedStartPeriod(null);
                        setSelectedEndPeriod(null);
                        notify();
                      }
                    }}
                    disabled={!selectedStartPeriod || !selectedEndPeriod || loading}
                    className="w-100"
                  >
                    Aggiungi Mesi
                  </Button>
                </div>
              </Col>

              <Col xs={12} md={2} className="mb-2">
                <div className="d-flex flex-column gap-2">
                  <Button
                    variant="outline-danger"
                    size="md"
                    className="w-100"
                    onClick={() => {
                      if (isMounted.current) {
                        setRawRows([]);
                        setLoadedPeriods([]);
                        setAggregatedPeriods([]);
                        setFilteredRows([]);
                        setSelectedPeriod(null);
                        setSelectedStartPeriod(null);
                        setSelectedEndPeriod(null);
                        setActiveSearchType(null);
                      }
                    }}
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>

            {(loadedPeriods.length > 0 || aggregatedPeriods.length > 0) && (
              <Row className="mt-2">
                <Col>
                  <div className="d-flex flex-wrap gap-2">
                    {(() => {
                      const allPeriods = [];

                      loadedPeriods.forEach((period) => {
                        const label = monthYearOptions.find((opt) => areSameMonth(opt.value, period))?.label;
                        allPeriods.push({
                          sortKey: formatIsoDate(period),
                          label: label,
                          type: "single",
                          key: formatMonthKey(period),
                        });
                      });

                      aggregatedPeriods.forEach((aggregated, index) => {
                        allPeriods.push({
                          sortKey: formatIsoDate(aggregated.startDate),
                          label: aggregated.label,
                          type: "aggregated",
                          key: `aggregated-${index}`,
                        });
                      });

                      const sortedKeys = sortPeriodsChronologically(allPeriods.map((p) => p.sortKey));
                      const sortedPeriods = sortedKeys
                        .map((sortKey) => allPeriods.find((p) => p.sortKey === sortKey))
                        .filter(Boolean);

                      return sortedPeriods.map((period) => (
                        <Badge
                          key={period.key}
                          variant={period.type === "single" ? "info" : "success"}
                          className="px-3 py-2 m-1"
                        >
                          {period.label}
                        </Badge>
                      ));
                    })()}
                  </div>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </Card.Header>

      <Card.Body className="px-0">
        {filteredRows.length > 0 && (
          <DataCharts
            showLines={false}
            data={filteredRows}
            isMobile={isMobile}
            periodColumns={sortPeriodsChronologically([...new Set(rawRows.map((r) => r.periodo))])}
          />
        )}

        {filteredRows.length > 0 && rawRows.length > 0 && <ToastContainer />}

        {
        // Nascondo i pulsanti di tipo di dato
        }
        {false && rawRows.length > 0 && (
          
          <div className="m-3 p-3 rounded">
            <Row className="align-items-center">
              <Col xs={12} className="mb-2">
                <h6 className="mb-2">Tipo di dato:</h6>
                <ButtonGroup className="w-100">
                  {enabledDataTypes.ims && (
                  <Button
                    variant={enabledDataTypes.ims ? "dark" : "outline-dark"}
                    size="md"
                    className="font-weight-bold"
                    onClick={() => toggleDataType("ims")}
                  >
                    {enabledDataTypes.ims ? "✓" : ""} IMS
                  </Button>
                  )}
                  {enabledDataTypes.farmacie && (
                  <Button
                    variant={enabledDataTypes.farmacie ? "success" : "outline-success"}
                    size="md"
                    className="font-weight-bold"
                    onClick={() => toggleDataType("farmacie")}
                  >
                    {enabledDataTypes.farmacie ? "✓" : ""} Farmacie
                  </Button>
                  )}
                  {enabledDataTypes.online && (
                  <Button
                    variant={enabledDataTypes.online ? "info" : "outline-info"}
                    size="md"
                    className="font-weight-bold"
                    onClick={() => toggleDataType("online")}
                  >
                    {enabledDataTypes.online ? "✓" : ""} Online
                  </Button>
                  )}
                </ButtonGroup>
              </Col>
            </Row>
          </div>
          
        )}

        {filteredRows.length > 0 && rawRows.length > 0 && (
          <div className="m-3 p-3 rounded">
            <Row className="align-items-center">
              <Col xs={12} md={3} className="mb-2">
                <Button
                  variant={aggregationType === "line" ? "secondary" : "outline-secondary"}
                  size="md"
                  className="w-100 font-weight-bold"
                  onClick={() => setAggregationType("line")}
                >
                  Raggruppa per Linea
                </Button>
              </Col>
              <Col xs={12} md={3} className="mb-2">
                <Button
                  variant={aggregationType === "region" ? "primary" : "outline-primary"}
                  size="md"
                  className="w-100 font-weight-bold"
                  onClick={() => setAggregationType("region")}
                >
                  Raggruppa per Regione
                </Button>
              </Col>
              <Col xs={12} md={3} className="mb-2">
                <Button
                  variant={aggregationType === "province" ? "info" : "outline-info"}
                  size="md"
                  className="w-100 font-weight-bold"
                  onClick={() => setAggregationType("province")}
                >
                  Raggruppa per province
                </Button>
              </Col>
              <Col xs={12} md={3} className="mb-2">
                <Button
                  variant={aggregationType === "global" ? "warning" : "outline-warning"}
                  size="md"
                  className="w-100 font-weight-bold"
                  onClick={() => setAggregationType("global")}
                >
                  Raggruppa per prodotto
                </Button>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col xs={12} md={12} className="mb-2">
                {aggregationType !== null && (
                  <ImsPrintPdfButton
                    rows={filteredRows}
                    columns={printColumns}
                    title={`Report vendite${selectedUser?.label ? ` - ${selectedUser.label}` : ""}`}
                    disabled={filteredRows.length === 0}
                    onError={(e) => toast.error(e?.message || "Errore durante la preparazione del PDF")}
                    aggregationType={aggregationType}
                    isMobile={isMobile}
                    hiddenColumnNames={pdfHiddenColumnNames}
                    groupedData={
                      aggregationType === "region"
                        ? groupDataByRegion(filteredRows)
                        : aggregationType === "province"
                          ? groupDataByProvince(filteredRows)
                          : aggregationType === "line"
                            ? groupDataByLine(filteredRows)
                            : null
                    }
                  />
                )}
              </Col>
            </Row>
          </div>
        )}

        {filteredRows.length > 0 && rawRows.length > 0 && (
          <div className="m-3 p-3 border rounded">
            <h6 className="text-muted mb-2">Legenda</h6>
            <Row>
              <Col md={6}>
                <div className="d-flex align-items-center mb-1">
                  <Badge className="mr-3" variant="light" style={{ width: 40, height: 40, alignContent: "center" }}>
                    Diff.
                  </Badge>
                  <span className="text-muted">
                    <div className="text-muted">Differenza media tra l'ultimo e il primo periodo caricato</div>
                    <small>Formula: (somma_differenze) / (numero_passi) </small>
                  </span>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex align-items-center mb-1">
                  <Badge className="mr-3" variant="light" style={{ width: 40, height: 40, alignContent: "center" }}>
                    %
                  </Badge>
                  <span className="text-muted">
                    <div>Variazione percentuale tra l'ultimo e il primo periodo caricato</div>
                    <small>Formula: ((ultimo - primo) / |primo|) * 100</small>
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-primary me-3" role="status" />
            <span className="text-muted">Caricamento dati in corso...</span>
          </div>
        )}

        {(aggregationType === "global" || aggregationType === null) && (
          <ImsGrid
            rows={filteredRows.slice(0).sort((a, b) => a.productName.localeCompare(b.productName))}
            columns={dynamicColumns}
            columnExtensions={columnExtensions}
            isMobile={isMobile}
            CustomHeaderCell={CustomHeaderCell}
            RowComponent={RowComponent}
            AdvancedFilterCell={AdvancedFilterCell}
            regionOptions={regionOptions}
            provOptions={provOptions}
            skuOptions={skuOptions}
            monthYearOptions={monthYearOptions}
            ProductTypeProvider={ProductTypeProvider}
            TrendTypeProvider={TrendTypeProvider}
            DiffTypeProvider={DiffTypeProvider}
            PercentTypeProvider={PercentTypeProvider}
            NumberTypeProvider={NumberTypeProvider}
            CurrencyTypeProvider={CurrencyTypeProvider}
            regionName={null}
          />
        )}

        {aggregationType === "region" &&
          Object.entries(groupDataByRegion(filteredRows)).map(([regionName, regionRows]) => (
            <ImsGrid
              key={regionName}
              rows={regionRows.slice(0).sort((a, b) => a.productName.localeCompare(b.productName))}
              columns={dynamicColumns}
              columnExtensions={columnExtensions}
              isMobile={isMobile}
              CustomHeaderCell={CustomHeaderCell}
              RowComponent={RowComponent}
              AdvancedFilterCell={AdvancedFilterCell}
              regionOptions={regionOptions}
              provOptions={provOptions}
              skuOptions={skuOptions}
              monthYearOptions={monthYearOptions}
              ProductTypeProvider={ProductTypeProvider}
              TrendTypeProvider={TrendTypeProvider}
              DiffTypeProvider={DiffTypeProvider}
              PercentTypeProvider={PercentTypeProvider}
              NumberTypeProvider={NumberTypeProvider}
              CurrencyTypeProvider={CurrencyTypeProvider}
              regionName={regionName}
            />
          ))}

        {aggregationType === "province" &&
          Object.entries(groupDataByProvince(filteredRows)).map(([provName, provRows]) => (
            <ImsGrid
              key={provName}
              rows={provRows.slice(0).sort((a, b) => a.productName.localeCompare(b.productName))}
              columns={dynamicColumns}
              columnExtensions={columnExtensions}
              isMobile={isMobile}
              CustomHeaderCell={CustomHeaderCell}
              RowComponent={RowComponent}
              AdvancedFilterCell={AdvancedFilterCell}
              regionOptions={regionOptions}
              provOptions={provOptions}
              skuOptions={skuOptions}
              monthYearOptions={monthYearOptions}
              ProductTypeProvider={ProductTypeProvider}
              TrendTypeProvider={TrendTypeProvider}
              DiffTypeProvider={DiffTypeProvider}
              PercentTypeProvider={PercentTypeProvider}
              NumberTypeProvider={NumberTypeProvider}
              CurrencyTypeProvider={CurrencyTypeProvider}
              provName={provName}
            />
          ))}

        {aggregationType === "line" &&
          Object.entries(groupDataByLine(filteredRows)).map(([lineName, lineRows]) => (
            <ImsGrid
              key={lineName}
              rows={lineRows.slice(0).sort((a, b) => a.productName.localeCompare(b.productName))}
              columns={dynamicColumns}
              columnExtensions={columnExtensions}
              isMobile={isMobile}
              CustomHeaderCell={CustomHeaderCell}
              RowComponent={RowComponent}
              AdvancedFilterCell={AdvancedFilterCell}
              regionOptions={regionOptions}
              provOptions={provOptions}
              skuOptions={skuOptions}
              monthYearOptions={monthYearOptions}
              ProductTypeProvider={ProductTypeProvider}
              TrendTypeProvider={TrendTypeProvider}
              DiffTypeProvider={DiffTypeProvider}
              PercentTypeProvider={PercentTypeProvider}
              NumberTypeProvider={NumberTypeProvider}
              CurrencyTypeProvider={CurrencyTypeProvider}
              lineName={lineName}
            />
          ))}
      </Card.Body>
    </Card>
  );
};

export default ImsPage;
